"use client";

import React, { useMemo, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { Typography } from "@/components/ui";

type LearnArticle = {
  title: string;
  description: string;
  image: string;
  alt: string;
  link: string;
  category: string;
  readingTime?: string;
};

interface LearnListingProps {
  articles: LearnArticle[];
}

export function LearnListing({ articles }: LearnListingProps) {
  const [query, setQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("");

  const categories = useMemo(
    () => Array.from(new Set(articles.map((item) => item.category))).sort(),
    [articles],
  );

  const filtered = useMemo(() => {
    let current = [...articles];

    if (selectedCategory) {
      current = current.filter((item) => item.category === selectedCategory);
    }

    const q = query.trim().toLowerCase();
    if (q) {
      current = current.filter(
        (item) =>
          item.title.toLowerCase().includes(q) ||
          item.description.toLowerCase().includes(q),
      );
    }

    return current;
  }, [articles, query, selectedCategory]);

  return (
    <>
      <div className="relative w-full md:w-96 mb-4">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search articles, guides, integrations..."
          aria-label="Search articles, guides, and integrations"
          className="w-full rounded-md border border-gray-300 bg-white pl-11 pr-5 py-3 text-sm focus:border-accent focus:outline-none focus:ring-accent"
        />
        <svg
          className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M9 15.5C12.5899 15.5 15.5 12.5899 15.5 9C15.5 5.41015 12.5899 2.5 9 2.5C5.41015 2.5 2.5 5.41015 2.5 9C2.5 12.5899 5.41015 15.5 9 15.5Z"
            stroke="currentColor"
            strokeWidth="1.5"
          />
          <path
            d="M14 14L17 17"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-2">
        {categories.map((cat) => {
          const isActive = selectedCategory === cat;
          return (
            <button
              key={cat}
              type="button"
              onClick={() => setSelectedCategory((prev) => (prev === cat ? "" : cat))}
              aria-pressed={isActive}
              className={[
                "rounded-md border px-3 py-1 text-xs transition-colors",
                isActive
                  ? "border-accent bg-accent text-white"
                  : "border-slate-200 bg-secondary/5 hover:border-accent",
              ].join(" ")}
            >
              {cat}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 gap-8 md:grid-cols-3 mt-10">
        {filtered.map((item, index) => (
          <Link
            key={index}
            href={item.link}
            className="group"
          >
            <div className="relative aspect-video overflow-hidden">
              <Image
                src={item.image}
                alt={item.alt}
                title={item.alt}
                width={300}
                height={200}
                className="h-auto w-full rounded-xl object-cover"
                sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                loading={index < 6 ? "eager" : "lazy"}
                priority={index < 6}
              />
            </div>
            <div className="py-5">
              {item.category && (
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <span className="inline-flex items-center rounded-full bg-secondary/10 text-secondary px-3 py-1 text-xs font-medium">
                    {item.category}
                  </span>
                  {item.readingTime && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-slate-50 px-3 py-1 text-[11px] font-medium text-slate-600">
                      <svg
                        className="h-3 w-3"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                      >
                        <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.2" />
                        <path
                          d="M8 4.5V8.2L10.5 9.5"
                          stroke="currentColor"
                          strokeWidth="1.2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <span>{item.readingTime}</span>
                    </span>
                  )}
                </div>
              )}
              <Typography variant="h4" as="h2" className="mb-4  font-semibold">
                {item.title}
              </Typography>
              <Typography
                variant="small"
                as="p"
                className="mb-4 line-clamp-2 min-h-[40px]"
              >
                {item.description}
              </Typography>
              {/* <div className="flex items-center gap-6 text-sm text-slate-500">
                <span className="inline-flex items-center gap-1">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    className="h-4 w-4"
                    aria-hidden="true"
                  >
                    <path
                      d="M12 6v6l3 3"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <circle
                      cx="12"
                      cy="12"
                      r="8"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      fill="none"
                    />
                  </svg>
                  <span>{item.readingTime}</span>
                </span>
                <span className="inline-flex items-center gap-1">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    className="h-4 w-4"
                    aria-hidden="true"
                  >
                    <path
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      fill="none"
                    />
                    <circle
                      cx="12"
                      cy="12"
                      r="3"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      fill="none"
                    />
                  </svg>
                  <span>
                    {(viewCounts[item.link] ?? item.views).toLocaleString()}{" "}
                    views
                  </span>
                </span>
              </div> */}
            </div>
          </Link>
        ))}
      </div>
    </>
  );
}

